import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import i18n from '../../../i18n';
import Conversation from '../../../models/Conversation';
import User from '../../../models/User';
import { CurrencyFormatter } from '../../../utils/Intl';
import RequestApprovalPopupLayout from './RequestApprovalPopupLayout';

const ModificationReceiverApprovalPopup = ({
    show,
    closeReceiverApprovalModal,
    conversation,
    user,
    approveModificationRequest
}) => {
    const [displayModal, setDisplayModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const shouldSetSubscriptionAutoRenew = () => {
        const modificator = conversation.getSimpleExchangeModificator();
        if (!modificator) {
            return false;
        }

        const subscription = user.get('subscription');
        return Boolean(
            subscription &&
                !subscription.auto_renew &&
                moment(modificator.get('end_on')).isAfter(moment(subscription.end_on))
        );
    };

    const shouldBuyGP = () => {
        const modificator = conversation.getSimpleExchangeModificator();
        if (!modificator) {
            return false;
        }

        return (
            modificator.get('guest').get('id') === user.get('id') &&
            modificator.get('gp_balance') > user.get('number_guestpoint')
        );
    };

    const renderBlockContent = () => {
        const modificator = conversation.getSimpleExchangeModificator();

        if (!modificator) {
            return null;
        }

        if (shouldSetSubscriptionAutoRenew() && shouldBuyGP()) {
            return (
                <div
                    className="content-text"
                    dangerouslySetInnerHTML={{
                        __html: i18n.t('messaging:receiver_warning_AR_GP', {
                            amount: modificator.get('gp_balance') - user.get('number_guestpoint'),
                            price: CurrencyFormatter.format(
                                modificator.getTotalToPay(true),
                                user.get('currency')
                            )
                        })
                    }}
                />
            );
        } else if (shouldSetSubscriptionAutoRenew()) {
            return (
                <div
                    className="content-text"
                    dangerouslySetInnerHTML={{
                        __html: i18n.t('messaging:receiver_warning_AR')
                    }}
                />
            );
        } else if (shouldBuyGP()) {
            return (
                <div
                    className="content-text"
                    dangerouslySetInnerHTML={{
                        __html: i18n.t('messaging:receiver_warning_GP', {
                            amount: modificator.get('gp_balance') - user.get('number_guestpoint'),
                            price: CurrencyFormatter.format(
                                modificator.getTotalToPay(true),
                                user.get('currency')
                            )
                        })
                    }}
                />
            );
        }
    };

    const closeModal = useCallback(() => {
        closeReceiverApprovalModal();
    }, [closeReceiverApprovalModal]);

    const approve = useCallback(() => {
        setIsLoading(true);
        approveModificationRequest().then(
            () => {
                setIsLoading(false);
                closeModal();
                document.location.href = `${i18n.t('exchange:conversation.url', {
                    id: conversation.id
                })}`;
            },
            (error) => {
                setIsLoading(false);
                closeModal();
                console.error(`[ERROR: APPROVE MODIFICATION REQUEST]: ${error}`);
            }
        );
    }, [approveModificationRequest]);

    useEffect(() => {
        setDisplayModal(show);
    }, [show]);

    return (
        <RequestApprovalPopupLayout show={displayModal}>
            <div className="block-content">
                <div className="content-title">{i18n.t('common:important')}</div>
                {renderBlockContent()}
            </div>
            <div className="block-cta">
                <button
                    className={classNames('hends-primary confirm btn-ajax', {
                        sending: isLoading
                    })}
                    id="accept-receiver"
                    onClick={approve}
                >
                    {i18n.t('exchange:understood')}
                </button>
                <button className="hends-secondary cancel" id="cancel-receiver" onClick={closeModal}>
                    {i18n.t('common:cancel')}
                </button>
            </div>
        </RequestApprovalPopupLayout>
    );
};

ModificationReceiverApprovalPopup.propTypes = {
    show: PropTypes.bool.isRequired,
    closeReceiverApprovalModal: PropTypes.func.isRequired,
    conversation: PropTypes.instanceOf(Conversation).isRequired,
    user: PropTypes.instanceOf(User).isRequired,
    approveModificationRequest: PropTypes.func.isRequired
};

export default ModificationReceiverApprovalPopup;
